import { US_COUNTRY_ID } from "@daytrip/legacy-config";
import { Expose, Transform, Type } from "class-transformer";
import {
    ArrayNotEmpty,
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsInt,
    IsNotEmpty,
    IsNumber,
    IsOptional,
    IsString,
    IsUUID,
    Length,
    Max,
    Min,
    ValidateIf,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { GField, GInputType, GObjectType } from "../graphql";
import {
    CDBaseEntity,
    CDColumn,
    CDCreateDateColumn,
    CDEntity,
    CDObjectIdColumn,
    getTransformFromLocalizedField,
} from "../orm";
import { transformNameToMachineName } from "../transformers/transformNameToMachineName";
import { TransformToDate } from "../transformers/transformToDate";
import { getPrimaryLocationImage } from "../utils/getPrimaryLocationImage";
import { IsTrimmed } from "../validators/IsTrimmed";

import { LocalizedField } from "./LocalizedField";
import { LocationImage } from "./LocationImage";
import { LocationPosition } from "./LocationPosition";
import type { MongoPointLocation } from "./MongoPointLocation";
import { Position } from "./Position";
import { Season } from "./Season";

export const DEFAULT_LOCATION_RADIUS_KM = 15;

@CDEntity("locations")
@GObjectType("Location")
@GInputType("LocationInput")
export class Location extends CDBaseEntity {
    @GField()
    @CDObjectIdColumn()
    @IsUUID("4")
    @Expose()
    public _id: string = uuid();

    // Text Fields

    @GField()
    @CDColumn()
    @Length(1, 64)
    @IsString()
    @IsTrimmed()
    @IsDefined()
    @Expose()
    public name: string = "";

    @GField()
    @IsString()
    @Transform(getTransformFromLocalizedField("titleLocalized"))
    @Expose()
    public title: string;

    @GField()
    @IsString()
    @Transform(getTransformFromLocalizedField("perexLocalized"))
    @Expose()
    public perex: string;

    @GField()
    @IsString()
    @Transform(getTransformFromLocalizedField("descriptionLocalized"))
    @Expose()
    public description: string;

    // Localized Text Fields

    @CDColumn()
    @ValidateIf(
        (location: Location) =>
            (location.isLive || location.isReady) && (location.isLocation || location.isDestination),
    )
    @IsDefined()
    @ValidateNested()
    @Type(() => LocalizedField)
    @Expose()
    public nameLocalized: LocalizedField = new LocalizedField();

    @CDColumn()
    @ValidateIf((location: Location) => (location.isLive || location.isReady) && location.isLocation)
    @IsDefined()
    @ValidateNested()
    @Type(() => LocalizedField)
    @Expose()
    public titleLocalized: LocalizedField = new LocalizedField();

    @CDColumn()
    @ValidateIf((location: Location) => (location.isLive || location.isReady) && location.isLocation)
    @IsDefined()
    @ValidateNested()
    @Type(() => LocalizedField)
    @Expose()
    public descriptionLocalized: LocalizedField = new LocalizedField();

    @CDColumn()
    @ValidateIf((location: Location) => (location.isLive || location.isReady) && location.isLocation)
    @IsDefined()
    @ValidateNested()
    @Type(() => LocalizedField)
    @Expose()
    public perexLocalized: LocalizedField = new LocalizedField();

    // TODO: remove after FE decoupled from BE
    @GField()
    public image: string;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isGenerated: boolean = false; // the location was generated automatically and not updated since

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isVisited: boolean = false; // the location has been visited by a customer at least once

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isReviewed: boolean = false; // the location was manually reviewed by a content manager

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isReady: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isLive: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isDestination: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isLocation: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isAirport: boolean = false;

    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isHarbor: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isSuggested: boolean = false;

    @GField()
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isApiOnly: boolean = false; // the location can only be used by API requests, it won't appear on the website

    @CDColumn()
    @IsArray()
    @Expose()
    @Type(() => String)
    public localNames: string[] = [];

    @GField()
    @CDColumn()
    @Length(1, 64)
    @IsString()
    @IsDefined()
    @Expose()
    @Transform((value) => (value ? transformNameToMachineName(value) : ""))
    public machineName: string = "";

    @CDColumn()
    @IsArray()
    @Expose()
    @Type(() => String)
    public otherNames: string[] = [];

    @CDColumn()
    @IsArray()
    @Expose()
    @Type(() => String)
    public airportCodes: string[] = [];

    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public score: number = 0;

    @GField()
    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public defaultDuration: number = 60;

    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public note: string = "";

    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public address: string = "";

    @GField(() => LocationPosition)
    @CDColumn()
    @IsDefined()
    @Expose()
    @Type(() => LocationPosition)
    public position: LocationPosition;

    // don't know why positions in locationModel stored without id and what will be broken if this.position._id will exists
    public get positionWithId(): Position {
        return {
            latitude: this.position.latitude,
            longitude: this.position.longitude,
            _id: this._id,
        } as Position;
    }

    // mongo native location for fast queries
    public geolocation: MongoPointLocation;

    @GField(() => String)
    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public timezone: string = "";

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Position)
    public entrancePositions: Position[] = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Position)
    public parkingPositions: Position[] = [];

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public countryId: string;

    @Expose()
    public countryName?: string;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public regionId?: string;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public entrancePrice: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public entranceFee: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public parkingPrice: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public parkingFee: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public additionalPrice: number = 0;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public additionalFee: number = 0;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Season)
    public seasons: Season[] = [];

    @CDColumn()
    @IsString()
    @Expose()
    public websiteUrl?: string;

    @CDColumn()
    @ArrayNotEmpty()
    @ValidateIf((location: Location) => location.isLive || location.isReady)
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => LocationImage)
    public images: LocationImage[] = [];

    @IsString()
    @IsDefined()
    @Expose()
    @Transform((_, obj) =>
        obj.images == null || obj.images.length === 0
            ? ""
            : getPrimaryLocationImage(obj.images as Array<LocationImage>),
    )
    public imageUrl: string;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public tagIds: string[] = [];

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public createdBy?: string;

    @CDCreateDateColumn()
    @IsDate()
    @IsDefined()
    @Expose()
    @TransformToDate
    public createdAt: Date = new Date();

    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Min(0.01)
    @Max(100)
    @Expose()
    @IsOptional()
    public radiusKm?: number;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public ordersCount: number = 0;

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public ticketsUrl?: string;

    @GField({ nullable: true })
    @CDColumn()
    @ValidateIf((location: Location) => location.countryId === US_COUNTRY_ID)
    @IsDefined({ always: true })
    @IsString({ always: true })
    @IsNotEmpty({ always: true })
    @Expose()
    public state?: string;

    @GField({ nullable: true })
    @CDColumn()
    @ValidateIf((location: Location) => location.countryId === US_COUNTRY_ID)
    @IsDefined({ always: true })
    @IsString({ always: true })
    @IsNotEmpty({ always: true })
    @Expose()
    public stateCode?: string;
}
